import Dialog from "@mui/material/Dialog";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import {
  Box,
  ButtonPrimary,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../components/utils";
import { formatDisplayDate } from "../../utils/date";
import AddOrEditDesignatedPersons from "./AddOrEditDesignatedPersons";
import {
  useGetDesignatedPersons,
  useDeleteDesignatedPerson,
} from "../../queries/designatedPerson";
import { CTADropdown } from "../../components/shared/Dropdown";

function DesignatedPersons() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const gridApi = useRef<GridApi | null>(null);
  const { data: _designatedPersons } = useGetDesignatedPersons();

  const [dialog, setDialog] = useState<{
    open: boolean;
    mode?: "Add" | "Edit";
    data?: any;
  }>({
    open: false,
    mode: "Add",
    data: undefined,
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    data?: any;
  }>({
    open: false,
    data: undefined,
  });

  const designatedPersonsData = useMemo(
    () =>
      _designatedPersons?.map((item: any) => ({
        ...item,
        createdAt: item.createdAt ? formatDisplayDate(item.createdAt) : "",
        noOfEmployees: item.employeesList?.length || 0,
      })),
    [_designatedPersons]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  useEffect(() => {
    if (designatedPersonsData !== undefined) {
      setFilteredRowData(designatedPersonsData);
    }
  }, [designatedPersonsData]);

  function handleAction(
    row: any,
    action: { name: string; disabled?: boolean }
  ) {
    if (action.name === "Edit List" && !action.disabled) {
      setDialog({ open: true, mode: "Edit", data: row });
    } else if (action.name === "Delete List" && !action.disabled) {
      setDeleteDialog({ open: true, data: row });
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "NAME",
        field: "name",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "NO. OF EMPLOYEES",
        field: "noOfEmployees",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "CREATED AT",
        field: "createdAt",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        resizable: false,
        sortable: false,
        menuTabs: [],
        cellRenderer: CTADropdown,
        cellRendererParams: (params: any) => ({
          actions: [
            { name: "Edit List", disabled: isUserReadOnly },
            { name: "Delete List", disabled: isUserReadOnly },
          ],
          onAction: (action: any) => handleAction(params.data, action),
        }),
      },
    ],
    [isUserReadOnly]
  );

  const [filteredRowData, setFilteredRowData] = useState<any>(
    designatedPersonsData
  );

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const { mutate: deleteDesignatedPerson } = useDeleteDesignatedPerson();

  function deleteDesignatedPersonEntry() {
    deleteDesignatedPerson(deleteDialog.data?.id || "", {
      onSuccess: () => {
        toast("Designated person deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDeleteDialog({ open: false });
      },
      onError: (err: any) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setDeleteDialog({ open: false });
      },
    });
  }

  return (
    <Box className="w-full p-4 bg-white rounded-lg ">
      <HStack className="items-center justify-between mb-8">
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">
            Designated Persons
          </p>
          <p className="text-xs font-medium text-gray-light">
            {filteredRowData?.length} Designated Persons
          </p>
        </VStack>
        <ButtonPrimary
          disabled={isUserReadOnly}
          onClick={() => {
            setDialog({ open: true });
          }}
        >
          Add Designated Person
        </ButtonPrimary>
        <Dialog open={dialog.open} maxWidth="md" fullWidth>
          <AddOrEditDesignatedPersons
            onClose={() => setDialog({ open: false })}
            mode={dialog.mode || "Add"}
            data={dialog.data}
          />
        </Dialog>
      </HStack>

      <HStack className="justify-between w-full">
        <Box
          style={{
            height: "400px",
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            rowData={filteredRowData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            animateRows={true}
            pagination={true}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          />
        </Box>
      </HStack>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="md"
      >
        <AlertDialog
          onClose={() => setDeleteDialog({ open: false })}
          open={deleteDialog.open}
          message={"Do you want to delete this designated person?"}
          onPrimaryAction={deleteDesignatedPersonEntry}
          onSecondaryAction={() => setDeleteDialog({ open: false })}
        />
      </Dialog>
    </Box>
  );
}

export default DesignatedPersons;
